import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { NotificationBell } from '../NotificationBell';
import { GlobalSearch } from '../GlobalSearch';
import { AIChatbotWidget } from '../AIChatbotWidget';
import { toast } from 'sonner';
import {
  LayoutDashboard, ListTodo, Users, MapPin, Bot, LogOut, Menu, X,
  ClipboardList, CreditCard, Building2, Activity, Shield, FileDown,
  Calendar, Plug, ScrollText, Crown, Zap, Radar, Compass
} from 'lucide-react';

// Feature keys mapped to nav items — null = always visible (role controls access)
const FEATURE_NAV_MAP = {
  '/dashboard': null,
  '/activities': null,
  '/customers': null,
  '/tasks': 'task_management',
  '/my-tasks': 'task_management',
  '/calendar': 'calendar',
  '/users': null,
  '/team': null,
  '/live-tracking': 'gps_live_tracking',
  '/visit-optimizer': 'nearby_scan',
  '/ai-assistant': 'ai_assistant',
  '/integrations': 'crm_integrations',
  '/audit-logs': 'audit_logs',
  '/subscription': null,
};

const ALL_NAV_ITEMS = {
  admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Activity, label: 'Activities', path: '/activities' },
    { icon: Building2, label: 'Customers', path: '/customers' },
    { icon: ListTodo, label: 'Tasks', path: '/tasks' },
    { icon: Calendar, label: 'Calendar', path: '/calendar' },
    { icon: Shield, label: 'Users', path: '/users' },
    { icon: Radar, label: 'Live Tracking', path: '/live-tracking' },
    { icon: Compass, label: 'Visit Optimizer', path: '/visit-optimizer' },
    { icon: Bot, label: 'AI Assistant', path: '/ai-assistant' },
    { icon: Plug, label: 'Integrations', path: '/integrations' },
    { icon: ScrollText, label: 'Audit Logs', path: '/audit-logs' },
    { icon: CreditCard, label: 'Subscription', path: '/subscription' },
  ],
  owner: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Activity, label: 'Activities', path: '/activities' },
    { icon: Building2, label: 'Customers', path: '/customers' },
    { icon: ListTodo, label: 'Tasks', path: '/tasks' },
    { icon: Calendar, label: 'Calendar', path: '/calendar' },
    { icon: Shield, label: 'Users', path: '/users' },
    { icon: Radar, label: 'Live Tracking', path: '/live-tracking' },
    { icon: Compass, label: 'Visit Optimizer', path: '/visit-optimizer' },
    { icon: Bot, label: 'AI Assistant', path: '/ai-assistant' },
    { icon: Plug, label: 'Integrations', path: '/integrations' },
    { icon: ScrollText, label: 'Audit Logs', path: '/audit-logs' },
    { icon: CreditCard, label: 'Subscription', path: '/subscription' },
  ],
  manager: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Activity, label: 'Activities', path: '/activities' },
    { icon: Building2, label: 'Customers', path: '/customers' },
    { icon: ListTodo, label: 'Tasks', path: '/tasks' },
    { icon: Calendar, label: 'Calendar', path: '/calendar' },
    { icon: Users, label: 'Team', path: '/team' },
    { icon: Radar, label: 'Live Tracking', path: '/live-tracking' },
    { icon: Compass, label: 'Visit Optimizer', path: '/visit-optimizer' },
    { icon: Bot, label: 'AI Assistant', path: '/ai-assistant' },
    { icon: Plug, label: 'Integrations', path: '/integrations' },
    { icon: ScrollText, label: 'Audit Logs', path: '/audit-logs' },
    { icon: CreditCard, label: 'Subscription', path: '/subscription' },
  ],
  salesperson: [
    { icon: LayoutDashboard, label: 'My Dashboard', path: '/dashboard' },
    { icon: Activity, label: 'My Activities', path: '/activities' },
    { icon: Building2, label: 'Customers', path: '/customers' },
    { icon: ClipboardList, label: 'My Tasks', path: '/my-tasks' },
    { icon: Calendar, label: 'Calendar', path: '/calendar' },
    { icon: Compass, label: 'Visit Optimizer', path: '/visit-optimizer' },
    { icon: Bot, label: 'AI Assistant', path: '/ai-assistant' },
  ],
};

const TIER_COLORS = {
  starter: '#6366f1',
  professional: '#002FA7',
  enterprise: '#f59e0b',
};

const TIER_ICONS = {
  starter: Zap,
  professional: Crown,
  enterprise: Building2,
};

export function Sidebar() {
  const { user, logout, isOwner } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tierData, setTierData] = useState(null);

  const fetchTier = useCallback(async () => {
    try {
      const res = await subscriptionApi.getMyTier();
      setTierData(res.data);
    } catch {
      // Silent fail
    }
  }, []);

  useEffect(() => { fetchTier(); }, [fetchTier]);

  const handleTierSwitch = async (tier) => {
    try {
      await subscriptionApi.switchTier(tier);
      const res = await subscriptionApi.getMyTier();
      setTierData(res.data);
      toast.success(`Switched to ${tier.charAt(0).toUpperCase() + tier.slice(1)} edition`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch tier');
    }
  };

  const role = user?.role || 'salesperson';
  const allItems = ALL_NAV_ITEMS[role] || ALL_NAV_ITEMS.salesperson;
  const features = tierData?.features || [];

  // Filter nav items based on tier features — owners also respect the active tier view
  const items = allItems.filter(item => {
    const requiredFeature = FEATURE_NAV_MAP[item.path];
    if (!requiredFeature) return true; // always visible (dashboard, activities, customers, subscription)
    return features.includes(requiredFeature);
  });

  const tier = tierData?.tier || 'starter';
  const TierIcon = TIER_ICONS[tier] || Zap;
  const tierColor = TIER_COLORS[tier] || '#6366f1';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U';

  const navContent = (
    <div className="flex flex-col h-full bg-white border-r border-gray-100">
      {/* Logo */}
      <div className="h-16 flex items-center gap-2.5 px-4 border-b border-gray-100 flex-shrink-0">
        <div className="w-8 h-8 rounded-sm bg-[#002FA7] flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">N</span>
        </div>
        {!collapsed && <span className="text-sm font-bold tracking-tight whitespace-nowrap">AIVORA NEXUS</span>}
      </div>

      {/* Owner Edition Switcher */}
      {isOwner && !collapsed && tierData && (
        <div className="mx-3 mt-3 p-2.5 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60" data-testid="owner-tier-switcher">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Crown className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-semibold text-amber-800">Owner View</span>
          </div>
          <Select value={tier} onValueChange={handleTierSwitch}>
            <SelectTrigger className="h-7 text-xs bg-white border-amber-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter Edition</SelectItem>
              <SelectItem value="professional">Professional Edition</SelectItem>
              <SelectItem value="enterprise">Enterprise Edition</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tier Badge */}
      {!collapsed && (
        <div className="mx-3 mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md" style={{ backgroundColor: `${tierColor}10` }}>
          <TierIcon className="w-3.5 h-3.5" style={{ color: tierColor }} />
          <span className="text-xs font-medium capitalize" style={{ color: tierColor }}>{tier}</span>
        </div>
      )}

      {/* Nav Items */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                isActive
                  ? 'bg-[#002FA7]/10 text-[#002FA7] font-medium'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? item.label : undefined}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User Footer */}
      <div className="border-t border-gray-100 p-3 flex-shrink-0">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <Avatar className="h-9 w-9 flex-shrink-0">
            <AvatarFallback className="bg-[#002FA7]/10 text-[#002FA7] text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate capitalize">{user?.role}</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className={`text-gray-400 hover:text-red-500 ${collapsed ? 'p-1' : ''}`}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-gray-200"
        onClick={() => setMobileOpen(true)}
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 shadow-xl">{navContent}</div>
          <button
            className="absolute top-4 right-4 p-2 bg-white rounded-lg shadow"
            onClick={() => setMobileOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className={`hidden lg:block fixed left-0 top-0 bottom-0 z-30 transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}>
        {navContent}
        <button
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 text-gray-400"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? '>' : '<'}
        </button>
      </div>
    </>
  );
}

export function MainLayout({ children }) {
  const [collapsed] = useState(false);
  return (
    <div className="min-h-screen bg-gray-50/50">
      <Sidebar />
      {/* Top Bar */}
      <div className={`fixed top-0 right-0 z-20 h-16 bg-white/80 backdrop-blur-xl border-b border-gray-100 flex items-center justify-end gap-3 px-6 transition-all duration-200 ${collapsed ? 'left-16' : 'left-0 lg:left-60'}`}>
        <GlobalSearch />
        <NotificationBell />
      </div>
      <main className={`pt-20 pb-20 lg:pb-8 px-4 sm:px-6 lg:px-8 transition-all duration-200 ${collapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        {children}
      </main>
      <AIChatbotWidget />
    </div>
  );
}
